import { type ReactNode } from 'react';
import { ChevronRightIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export interface QueueRow {
  readonly id: string;
  readonly label: ReactNode;
  readonly meta?: ReactNode;
  readonly accent?: 'urgent' | 'normal';
}

export interface QueueCardCta {
  readonly label: string;
  readonly onClick: () => void;
}

export interface QueueCardProps {
  readonly title: ReactNode;
  /** Optional eyebrow label rendered above the title in mono caps. */
  readonly eyebrow?: ReactNode;
  readonly rows: readonly QueueRow[];
  readonly onRowClick?: (id: string) => void;
  /** Custom empty-state node — wins over the default dashed placeholder. */
  readonly empty?: ReactNode;
  readonly cta?: QueueCardCta;
  readonly className?: string;
  /** Maximum rows to render (default 5). */
  readonly maxRows?: number;
}

/**
 * F06 — dark-panel "Needs your eyes" callout with accent-arrow rows.
 *
 * - Dark background (`bg-ink`, white text), title at top, max 5 rows by default,
 *   each row has a chevron-right accent.
 * - Urgent rows tint with `danger`.
 * - Bottom optional CTA. Empty state renders the `empty` ReactNode (dashed border).
 *
 * Also used by F09 admin queue.
 */
export function QueueCard({
  title,
  eyebrow,
  rows,
  onRowClick,
  empty,
  cta,
  className,
  maxRows = 5,
}: QueueCardProps) {
  const visible = rows.slice(0, maxRows);
  const isEmpty = visible.length === 0;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl bg-ink text-bg p-5 flex flex-col gap-3',
        className,
      )}
    >
      {/* Accent glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -end-24 size-64 rounded-full"
        style={{
          background:
            'radial-gradient(circle, color-mix(in srgb, var(--color-accent) 35%, transparent) 0%, transparent 60%)',
        }}
      />
      <div className="relative flex flex-col gap-3">
        {eyebrow ? (
          <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-accent font-mono">
            {eyebrow}
          </div>
        ) : null}
        <div className="text-[18px] font-bold tracking-tight leading-tight">{title}</div>

        {isEmpty ? (
          (empty ?? (
            <div className="rounded-lg border border-dashed border-white/20 p-4 text-[12.5px] text-bg/70 text-center">
              Nothing here for now.
            </div>
          ))
        ) : (
          <ul role="list" className="flex flex-col gap-2 mt-1">
            {visible.map((row) => {
              const isUrgent = row.accent === 'urgent';
              const Comp = onRowClick ? 'button' : 'div';
              return (
                <li key={row.id}>
                  <Comp
                    {...(onRowClick
                      ? {
                          type: 'button' as const,
                          onClick: () => onRowClick(row.id),
                          'aria-label':
                            typeof row.label === 'string' ? row.label : 'Open queue item',
                        }
                      : {})}
                    className={cn(
                      'w-full text-start rounded-lg border border-white/10 bg-white/[0.06] px-3 py-2.5',
                      'flex items-center gap-3',
                      onRowClick &&
                        'transition-colors duration-[120ms] hover:bg-white/[0.10] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/40 cursor-pointer',
                      isUrgent && 'border-danger/40 bg-danger/[0.12]',
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div
                        className={cn(
                          'text-[13px] font-semibold truncate',
                          isUrgent ? 'text-danger' : 'text-bg',
                        )}
                      >
                        {row.label}
                      </div>
                      {row.meta ? (
                        <div className="text-[10.5px] font-mono uppercase tracking-wider text-bg/70 truncate mt-0.5">
                          {row.meta}
                        </div>
                      ) : null}
                    </div>
                    <span
                      aria-hidden="true"
                      className={cn(
                        'shrink-0 inline-flex items-center justify-center',
                        isUrgent ? 'text-danger' : 'text-accent',
                      )}
                    >
                      <ChevronRightIcon size={16} color="currentColor" />
                    </span>
                  </Comp>
                </li>
              );
            })}
          </ul>
        )}

        {cta ? (
          <button
            type="button"
            onClick={cta.onClick}
            className={cn(
              'mt-2 inline-flex items-center justify-center gap-1 rounded-lg',
              'bg-accent text-accent-ink font-semibold text-[13px] h-9 px-4',
              'transition-colors duration-[120ms] hover:opacity-90 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/40',
            )}
          >
            {cta.label}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default QueueCard;
